<template>
  <div class="admin-page revenue-page" v-loading="loading">
    <!-- ============ 页头 ============ -->
    <div class="page-head">
      <div class="head-text">
        <h1>收益统计</h1>
        <p>
          {{ scopeLabel }} · {{ summary.month || '' }}
          <span v-if="summary.inProgress" class="tag tag--live">进行中 · 未结账</span>
          <span v-else-if="summary.locked" class="tag tag--locked">
            <i class="el-icon-lock" />已结账
          </span>
          <span v-else class="tag tag--draft">未结账</span>
        </p>
      </div>
      <div class="head-actions">
        <el-date-picker
          v-model="monthPicker"
          type="month"
          size="small"
          value-format="yyyy-MM"
          :picker-options="monthOptions"
          :clearable="false"
          class="month-picker"
          @change="fetchAll"
        />
        <el-select
          v-if="schoolOptions.length"
          v-model="schoolId"
          size="small"
          class="scope-select"
          @change="fetchAll"
        >
          <el-option label="全部学校" value="" />
          <el-option
            v-for="item in schoolOptions"
            :key="String(item.id)"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </div>
    </div>

    <!-- ============ 视觉重心：深色主指标区（本屏唯一深色块） ============ -->
    <div class="hero">
      <div class="hero__main">
        <div class="hero__label">本月收益 · 仅用户充值</div>
        <div class="hero__amount">
          <small>¥</small>{{ money(summary.totalAmount) }}
        </div>
        <div class="hero__delta" v-if="summary.momRatio !== null && summary.momRatio !== undefined">
          较上月 <span :class="summary.momRatio >= 0 ? 'up' : 'down'">{{ ratioText(summary.momRatio) }}</span>
          <em>上月 ¥{{ money(summary.lastMonthTotal) }}</em>
        </div>
        <div class="hero__delta" v-else>
          <em>没有上月数据可比</em>
        </div>

        <!--
          ⛔ 主数字下面只放"同一个数的拆解"（人 / 单），不放别的收入项。
          原来这里并列了"广告收入"，那是错的：它不计入收益，并列会让人以为
          收益 = 充值 + 广告，然后自己算一遍发现对不上。
        -->
        <div class="hero__split">
          <div class="hero__cell">
            <div class="k">付费人数</div>
            <div class="v">{{ num(summary.payingUserCount) }} <small>人</small></div>
            <div class="s">同一个人多单只算一次</div>
          </div>
          <div class="hero__cell">
            <div class="k">订单数</div>
            <div class="v">{{ num(summary.orderCount) }} <small>单</small></div>
            <div class="s">已排除未支付与后台充值</div>
          </div>
          <div class="hero__cell">
            <div class="k">客单价</div>
            <div class="v">¥{{ money(arpu) }}</div>
            <div class="s">总收益 ÷ 付费人数</div>
          </div>
        </div>
      </div>

      <!-- 右侧：收益 TOP5 迷你横条（无坐标轴，纯长度对比） -->
      <div class="hero__rank" v-if="topSchools.length">
        <div class="hero__rank-title">{{ isSuper ? '收益 TOP5' : '本校收益' }}</div>
        <div class="rank-row" v-for="(item, index) in topSchools" :key="String(item.schoolId)">
          <div class="rank-row__name">{{ item.schoolName }}</div>
          <div class="rank-row__track">
            <div
              class="rank-row__bar"
              :class="{ 'is-first': index === 0 }"
              :style="{ width: barWidth(item.rechargeAmount, maxSchoolAmount) }"
            />
          </div>
          <div class="rank-row__value">¥{{ money(item.rechargeAmount) }}</div>
        </div>
      </div>
    </div>

    <!--
      ============ 不计入收益的参考数据 ============
      ⛔ 这里**没有广告**。白总要求：收益页只讲"用户购买爱豆"，不展示广告。
      广告收入原本有一张卡在这里，已删 —— 原因不只是"它不计收益"：
        · 它是流量主结算的钱，按完播估算分摊，管理员看到会问"这钱在哪"；
        · 它根本不在学校手里，放在学校的收益页上是误导；
        · 广告的事在「广告分析」页（仅超管）里讲，那边只讲行为不讲钱。
      剩下这几项都跟"爱豆"直接相关：赠送的豆、卡核销的豆、退款。
    -->
    <div class="ref-row">
      <div class="ref">
        <div class="ref__k">
          赠送爱豆
          <span class="ref__badge">不算收入</span>
        </div>
        <div class="ref__v num">{{ num(summary.giftBeanAmount) }} <small>豆</small></div>
        <div class="ref__s">折合约 ¥{{ money(summary.giftBeanValue) }} · 发行成本</div>
      </div>
      <div class="ref" v-if="Number(summary.cardAmount) > 0">
        <div class="ref__k">
          充值卡核销
          <span class="ref__badge">不计入收益</span>
        </div>
        <div class="ref__v num">¥{{ money(summary.cardAmount) }}</div>
        <div class="ref__s">与线下收款自行对账</div>
      </div>
      <div class="ref ref--muted">
        <div class="ref__k">退款</div>
        <div class="ref__v num">¥{{ money(summary.refundAmount) }}</div>
        <div class="ref__s">{{ num(summary.refundCount) }} 笔 · 不冲减收益</div>
      </div>
    </div>

    <!-- ============ 口径说明条（每个数字都能点开看构成） ============ -->
    <div class="notice-bar">
      <i class="el-icon-info" />
      <span>
        收益<b>只等于用户购买爱豆的充值</b>（已排除后台手工充值、未支付、退款订单）。
        <em>赠送爱豆、充值卡核销一律不计入收益，只作参考。</em>
        置顶费、取纸条费属爱豆消耗，钱在充值时已入账，不重复计算。
      </span>
      <button type="button" class="link-btn" @click="openOrders()">看订单明细</button>
    </div>

    <!-- ============ 学校明细 ============ -->
    <div class="r-card">
      <div class="r-card__head">
        <div class="r-card__title">各学校明细</div>
        <span class="r-card__hint">
          共 {{ schools.length }} 行 · 分校之和 + 未归属 = 全平台合计
        </span>
      </div>

      <div v-if="!schools.length" class="r-empty">
        <i class="el-icon-wallet" />
        <p>{{ summary.month }} 没有收益数据</p>
        <span>如果确认这个月有充值，点下面「重算」让系统重新聚合</span>
      </div>

      <table v-else class="r-table">
        <thead>
          <tr>
            <th>学校</th>
            <th class="is-revenue">收益（充值）</th>
            <th>占比</th>
            <th>订单</th>
            <th>付费人数</th>
            <!-- ⛔ "广告分摊"列已删除：这一页不展示任何广告数据 -->
            <th class="is-ref">赠送豆</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in schools" :key="String(item.schoolId)" @click="openOrders(item)">
            <td>
              <span class="cell-name">{{ item.schoolName }}</span>
            </td>
            <!-- 收益列：本表唯一有分量的数字 -->
            <td class="num cell-total">¥{{ money(item.rechargeAmount) }}</td>
            <td>
              <div class="share-bar">
                <div class="share-bar__track">
                  <div class="share-bar__in" :style="{ width: barWidth(item.rechargeAmount, maxSchoolAmount) }" />
                </div>
                <span class="share-bar__text">{{ percentText(item.share) }}</span>
              </div>
            </td>
            <td class="num">{{ num(item.orderCount) }}</td>
            <td class="num">{{ num(item.payingUserCount) }}</td>
            <!-- 赠送列：⛔ 不是收入，灰掉。广告列已整列删除（收益页不展示广告） -->
            <td class="num cell-ref">{{ num(item.giftBeanAmount) }}</td>
          </tr>
        </tbody>
      </table>
      <div class="r-table__foot">
        收益列 = 该校用户购买爱豆的充值实收。灰字列是<b>不计入收益</b>的参考数据。
      </div>
    </div>

    <!-- ============ 赠送构成（成本参考，不是收入） ============ -->
    <div class="r-cols">
      <div class="r-card">
        <div class="r-card__head">
          <div class="r-card__title">赠送爱豆构成</div>
          <span class="r-card__hint">发行成本，不计入收益</span>
        </div>
        <div v-if="!giftBreakdown.length" class="r-empty r-empty--sm">
          <i class="el-icon-present" />
          <p>本月没有赠送记录</p>
        </div>
        <div v-else class="gift-list">
          <div class="gift" v-for="item in giftBreakdown" :key="item.type">
            <span class="gift__label">{{ item.label }}</span>
            <div class="gift__track">
              <div class="gift__bar" :style="{ width: barWidth(item.beanAmount, maxGiftBean) }" />
            </div>
            <span class="gift__value">{{ num(item.beanAmount) }} 豆 · {{ num(item.count) }} 笔</span>
          </div>
          <div class="gift-total">
            合计 <b>{{ num(summary.giftBeanAmount) }}</b> 豆，
            按 {{ beanPerYuan }} 豆 = 1 元折合约 <b>¥{{ money(summary.giftBeanValue) }}</b>
            <em>（这笔是成本，不是收入）</em>
          </div>
        </div>
      </div>

      <div class="r-card">
        <div class="r-card__head">
          <div class="r-card__title">对账口径</div>
          <span class="r-card__hint">每一项都能点开看明细</span>
        </div>
        <div class="recon">
          <div class="recon__row recon__row--sum">
            <span>收益（用户购买爱豆）</span>
            <b class="num">¥{{ money(summary.totalAmount) }}</b>
          </div>
          <div class="recon__row recon__row--muted">
            <span>未归属学校</span>
            <b class="num">¥{{ money(summary.unallocatedAmount) }}</b>
          </div>
          <div class="recon__row recon__row--muted">
            <span>退款（不冲减）</span>
            <b class="num">¥{{ money(summary.refundAmount) }}</b>
          </div>

          <div class="recon__divider">以下均不计入收益</div>

          <!-- ⛔ 广告收入这一行已删除（收益页不展示广告） -->
          <div class="recon__row recon__row--ref" v-if="Number(summary.cardAmount) > 0">
            <span>充值卡核销</span>
            <b class="num">¥{{ money(summary.cardAmount) }}</b>
          </div>
          <div class="recon__row recon__row--ref">
            <span>赠送爱豆等值（成本）</span>
            <b class="num">¥{{ money(summary.giftBeanValue) }}</b>
          </div>
          <p class="recon__tip">
            为什么这些不算：充值卡可能是线下已经收过钱，算进来就是同一笔钱算两次；
            赠送豆没产生任何支付流水。收益只认用户实际掏钱买的那一部分。
          </p>
        </div>
      </div>
    </div>

    <!-- ============ 底部操作条（仅超管） ============ -->
    <div v-if="isSuper" class="r-foot">
      <div class="r-foot__note">
        <b>历史月份的账需要先「重算」</b>才会生成快照（当月是实时算的，不用管）。<br>
        结账锁定后不能再重算，要改必须先解锁（会留审计记录）。
      </div>
      <div class="r-foot__btns">
        <button type="button" class="btn" :disabled="summary.locked || summary.inProgress" @click="doRecompute">
          重算本月
        </button>
        <button v-if="!summary.locked" type="button" class="btn btn--primary" :disabled="summary.inProgress" @click="doLock">
          结账锁定
        </button>
        <button v-else type="button" class="btn" @click="doUnlock">解锁</button>
      </div>
    </div>

    <!-- ============ 订单明细抽屉 ============ -->
    <el-drawer :visible.sync="ordersVisible" :with-header="false" size="720px" class="admin-drawer">
      <div class="drawer-body">
        <div class="drawer-head">
          <div>
            <h3>{{ ordersTitle }}</h3>
            <p>{{ summary.month }} · 收进来的每一笔钱</p>
          </div>
          <button type="button" class="drawer-close" @click="ordersVisible = false">
            <i class="el-icon-close" />
          </button>
        </div>
        <div v-if="!orders.length" class="r-empty r-empty--sm">
          <i class="el-icon-document" />
          <p>该范围没有订单</p>
        </div>
        <div v-else class="order-list">
          <div class="order" v-for="item in orders" :key="item.tradeNo">
            <div class="order__main">
              <span class="order__user">{{ item.nickname || '用户 #' + item.userId }}</span>
              <span class="order__channel">{{ channelLabel(item.payChannel) }}</span>
            </div>
            <div class="order__foot">
              <span class="order__no">{{ item.tradeNo }}</span>
              <span class="order__time">{{ formatTime(item.createdAt) }}</span>
              <span class="order__amount">¥{{ money(item.amount) }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-drawer>

    <!--
      ⛔ 「录入广告收入」弹窗已删除。
      收益页只讲用户购买爱豆，不涉及广告；广告收入既不计收益也不在这页展示，
      留个录入入口只会让人以为"广告收入该录这里"，然后困惑它为什么不进收益。
      广告的事在「广告分析」页（仅超管）里看，那边只讲行为不讲钱。
    -->
  </div>
</template>

<script>
import {
  getRevenueSummary,
  getRevenueOrders,
  recomputeRevenue,
  lockRevenue,
  unlockRevenue
} from '@/api/revenue'
import { getSchoolOptions } from '@/api/school'
import { getPublicConfigs } from '@/api/system'
import { setBeanRate } from '@/utils/bean'

export default {
  name: 'RevenueStats',
  data() {
    return {
      loading: false,
      monthPicker: '',
      schoolId: '',
      schoolOptions: [],
      scopeLabel: '全平台',
      summary: {},
      schools: [],
      giftBreakdown: [],
      beanPerYuan: 10,
      // 订单抽屉
      ordersVisible: false,
      ordersTitle: '',
      orders: [],
      monthOptions: {
        // 不能选未来月份：未来没有数据，选了只会让人以为系统坏了
        disabledDate(date) {
          return date.getTime() > Date.now()
        }
      }
    }
  },
  computed: {
    /**
     * 是否超管。
     * ⛔ 用 role 判断，不要用 roleLabel（中文标签）：
     *    标签文案改一次，这个判断就悄悄失效了，而失效的表现是
     *    "管理员也能看到结账按钮" —— 那种没人会立刻发现的问题。
     */
    isSuper() {
      const info = this.$store.state.adminInfo || {}
      return info.role === 'super'
    },
    /**
     * 客单价 = 收益 ÷ 付费人数。
     * ⛔ 分母是"付费人数"不是"订单数"：同一个人多单的情况很常见，
     *    按订单数算出来的是"单均价"，会低估真实付费能力。
     */
    arpu() {
      const users = Number(this.summary.payingUserCount) || 0
      const total = Number(this.summary.totalAmount) || 0
      if (users <= 0) {
        return 0
      }
      return total / users
    },
    topSchools() {
      if (!this.isSuper) {
        // 管理员只有自己那一所，不需要排行
        return this.schools.slice(0, 1)
      }
      return this.schools.filter(item => Number(item.rechargeAmount) > 0).slice(0, 5)
    },
    maxSchoolAmount() {
      // ⛔ 用 rechargeAmount 而不是 totalAmount：收益只等于充值，两者数值一致，
      //    但用前者表达意图更清楚（将来若真改了口径，排序依据不会默默跟着变错）
      const values = this.schools.map(item => Number(item.rechargeAmount) || 0)
      return Math.max(1, ...values)
    },
    maxGiftBean() {
      const values = this.giftBreakdown.map(item => Number(item.beanAmount) || 0)
      return Math.max(1, ...values)
    }
  },
  created() {
    // 默认上月：当月还没结账，看"上个月的完整账"更符合财务习惯
    const now = new Date()
    now.setMonth(now.getMonth() - 1)
    this.monthPicker = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

    this.loadBeanRate()
    this.loadSchoolOptions()
    this.fetchAll()
  },
  methods: {
    loadBeanRate() {
      getPublicConfigs().then(res => {
        const list = (res && res.data) || []
        const rate = list.find(item => item.configKey === 'bean.per_yuan')
        if (rate) {
          setBeanRate(rate.configValue)
          this.beanPerYuan = Number(rate.configValue) || 10
        }
      }).catch(() => {})
    },

    /**
     * 充值卡是否计入收益，由 computed cardCounted 判断（看 cardAmount 有没有值）。
     * 这里不需要额外拉配置。
     */
    loadSchoolOptions() {
      // 超管拿到全部学校；管理员只拿到授权的
      getSchoolOptions().then(res => {
        this.schoolOptions = (res && res.data) || []
      }).catch(() => {})
    },

    fetchAll() {
      if (!this.monthPicker) {
        return
      }
      this.loading = true
      const params = { month: this.monthPicker }
      if (this.schoolId !== '' && this.schoolId !== null) {
        params.schoolId = this.schoolId
      }
      getRevenueSummary(params).then(res => {
        const data = (res && res.data) || {}
        this.summary = data
        this.schools = data.schools || []
        this.giftBreakdown = data.giftBreakdown || []
        this.scopeLabel = data.scopeLabel || '全平台'
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    openOrders(school) {
      const params = { month: this.monthPicker, limit: 300 }
      if (school && school.schoolId) {
        params.schoolId = school.schoolId
        this.ordersTitle = school.schoolName + ' · 订单'
      } else if (this.schoolId !== '') {
        params.schoolId = this.schoolId
        this.ordersTitle = this.scopeLabel + ' · 订单'
      } else {
        this.ordersTitle = '全平台 · 订单'
      }
      this.orders = []
      this.ordersVisible = true
      getRevenueOrders(params).then(res => {
        this.orders = (res && res.data) || []
      }).catch(() => {})
    },

    doRecompute() {
      this.$confirm(
        `重算会把 ${this.monthPicker} 的所有数字按当前充值流水重新聚合。确定？`,
        '重算本月',
        { type: 'warning' }
      ).then(() => recomputeRevenue({ month: this.monthPicker }))
        .then(res => {
          this.$message.success((res && res.message) || '已重算')
          this.fetchAll()
        }).catch(() => {})
    },

    doLock() {
      this.$confirm(
        `结账锁定后，${this.monthPicker} 的数字不能再重算（要改必须先解锁）。请确认这个月的账已经核对过。`,
        '结账锁定',
        { type: 'warning', confirmButtonText: '确认结账' }
      ).then(() => lockRevenue({ month: this.monthPicker }))
        .then(() => {
          this.$message.success('已结账锁定')
          this.fetchAll()
        }).catch(() => {})
    },

    doUnlock() {
      this.$prompt('解锁后这个月可以重新计算。请填写原因（会记入审计日志）', '解锁', {
        inputPlaceholder: '例如：9 月有笔订单漏记了，需要重算',
        inputValidator: value => (value && value.trim().length >= 4) || '原因至少 4 个字'
      }).then(({ value }) => unlockRevenue({ month: this.monthPicker, reason: value }))
        .then(() => {
          this.$message.success('已解锁')
          this.fetchAll()
        }).catch(() => {})
    },

    /* ---------------- 格式化 ---------------- */

    money(value) {
      const n = Number(value)
      if (!isFinite(n)) {
        return '0.00'
      }
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    num(value) {
      const n = Number(value)
      if (!isFinite(n)) {
        return '0'
      }
      return n.toLocaleString('zh-CN')
    },

    /** 后端给的是 0~1 的小数 */
    percentText(value) {
      const n = Number(value)
      if (!isFinite(n) || n <= 0) {
        return '0%'
      }
      return (n * 100).toFixed(1) + '%'
    },

    ratioText(value) {
      const n = Number(value)
      if (!isFinite(n)) {
        return '—'
      }
      const sign = n >= 0 ? '+' : ''
      return sign + (n * 100).toFixed(1) + '%'
    },

    barWidth(value, max) {
      const ratio = (Number(value) || 0) / (max || 1)
      return Math.max(ratio * 100, ratio > 0 ? 3 : 0) + '%'
    },

    formatTime(value) {
      if (!value) {
        return ''
      }
      return String(value).replace('T', ' ').slice(0, 16)
    },

    channelLabel(channel) {
      if (!channel) {
        return '微信支付'
      }
      const map = {
        VIRTUAL: '小程序虚拟支付',
        WECHAT: '微信支付',
        ADMIN: '后台充值'
      }
      return map[channel.toUpperCase()] || channel
    }
  }
}
</script>

<style lang="scss" scoped>
.month-picker {
  width: 140px;
}

.scope-select {
  width: 190px;
}

/* ---------- 页头标签 ---------- */
.tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 19px;
  padding: 0 7px;
  margin-left: 8px;
  border-radius: 4px;
  font-size: 11px;

  i {
    font-size: 10px;
  }

  &--live {
    background: var(--rose-50);
    color: var(--rose-600);
    border: 1px solid #f6d6da;
  }

  &--locked {
    background: var(--ok-bg);
    color: var(--ok-text);
    border: 1px solid #d6e8de;
  }

  &--draft {
    background: var(--warn-bg);
    color: var(--warn-text);
    border: 1px solid #f0e3c4;
  }
}

/* ---------- 深色主指标区（本屏唯一深色块） ---------- */
.hero {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 32px;
  padding: 26px 28px;
  border-radius: var(--r-lg);
  background:
    radial-gradient(620px 280px at 88% -30%, rgba(225, 77, 104, 0.26) 0%, transparent 62%),
    linear-gradient(135deg, #241c28 0%, #171219 100%);
  box-shadow: 0 10px 30px -12px rgba(23, 18, 25, 0.42);
  margin-bottom: 14px;

  &__label {
    font-size: 12px;
    letter-spacing: 1.6px;
    color: rgba(255, 255, 255, 0.55);
  }

  &__amount {
    margin-top: 10px;
    font-size: 46px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.5px;
    color: #fff;
    font-variant-numeric: tabular-nums;

    small {
      font-size: 20px;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.7);
      margin-right: 4px;
    }
  }

  &__delta {
    margin-top: 10px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.55);

    .up {
      color: #ff9aae;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }

    .down {
      color: #8fd6b0;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }

    em {
      font-style: normal;
      margin-left: 8px;
      color: rgba(255, 255, 255, 0.38);
    }
  }

  &__split {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  &__cell {
    .k {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
    }

    .v {
      margin-top: 6px;
      font-size: 19px;
      font-weight: 500;
      color: #fff;
      font-variant-numeric: tabular-nums;

      small {
        font-size: 11px;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.45);
        margin-left: 3px;
      }
    }

    .s {
      margin-top: 5px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.4);
      line-height: 1.5;
    }
  }

  &__rank {
    position: relative;
    padding-left: 28px;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__rank-title {
    font-size: 12px;
    letter-spacing: 1.2px;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 16px;
  }
}

.est {
  font-style: normal;
  font-size: 10px;
  color: var(--gold);
  margin-left: 4px;
}

/* ---------- 不计入收益的参考数据条（有意放在 hero 外面） ---------- */
.ref-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.ref {
  background: #fff;
  border: 1px dashed var(--line);
  border-radius: var(--r-lg);
  padding: 14px 16px;

  &__k {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--ink-500);
  }

  /* 角标是这一组卡片存在的意义：一眼看出"这些不是钱" */
  &__badge {
    font-size: 10px;
    color: var(--ink-400);
    background: var(--ink-100);
    border-radius: 3px;
    padding: 0 4px;
    line-height: 15px;
  }

  &__v {
    margin-top: 8px;
    font-size: 20px;
    font-weight: 600;
    color: var(--ink-600);
    font-variant-numeric: tabular-nums;

    small {
      font-size: 11px;
      font-weight: 400;
      color: var(--ink-400);
      margin-left: 3px;
    }
  }

  &__s {
    margin-top: 6px;
    font-size: 11px;
    color: var(--ink-400);
    line-height: 1.5;
  }

  /* 退款那张卡再弱一档：它连"参考收入"都不是，只是对账信息 */
  &--muted {
    background: #fdfbfa;

    .ref__v {
      color: var(--ink-400);
      font-weight: 500;
    }
  }
}

/* ---------- 排行横条 ---------- */
.rank-row {
  display: grid;
  grid-template-columns: 92px 1fr 72px;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  &__name {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.76);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__track {
    height: 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.55);

    &.is-first {
      background: var(--rose-400);
    }
  }

  &__value {
    font-size: 12px;
    text-align: right;
    color: rgba(255, 255, 255, 0.62);
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- 口径说明条 ---------- */
.notice-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  font-size: 12px;
  color: var(--ink-500);
  line-height: 1.7;

  i {
    color: var(--ink-400);
    flex-shrink: 0;
  }

  b {
    color: var(--ink-900);
    font-weight: 500;
  }

  em {
    font-style: normal;
    color: var(--ink-400);
  }

  .link-btn {
    margin-left: auto;
    border: 0;
    background: transparent;
    color: var(--rose-600);
    font-size: 12px;
    cursor: pointer;
    flex-shrink: 0;
    font-family: inherit;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* ---------- 卡片 ---------- */
.r-card {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-card);
  margin-bottom: 14px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 15px 20px;
    border-bottom: 1px solid var(--ink-100);
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink-900);
  }

  &__hint {
    font-size: 12px;
    color: var(--ink-400);
  }
}

.r-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

/* ---------- 空态 ---------- */
.r-empty {
  padding: 44px 20px;
  text-align: center;

  i {
    font-size: 28px;
    color: var(--ink-300);
  }

  p {
    margin-top: 10px;
    font-size: 13px;
    color: var(--ink-500);
  }

  span {
    display: block;
    margin-top: 6px;
    font-size: 11px;
    color: var(--ink-400);
  }

  &--sm {
    padding: 28px 20px;
  }
}

/* ---------- 表格 ---------- */
.r-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 10px 18px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ink-400);
    text-align: right;
    background: #fdfbfa;
    white-space: nowrap;

    &:first-child {
      text-align: left;
    }
  }

  td {
    padding: 13px 18px;
    font-size: 13px;
    color: var(--ink-600);
    text-align: right;
    border-top: 1px solid var(--ink-100);

    &:first-child {
      text-align: left;
      color: var(--ink-900);
    }

    &.num {
      font-variant-numeric: tabular-nums;
    }
  }

  tbody tr {
    cursor: pointer;

    &:hover td {
      background: #fdf7f5;
    }

    &:hover .cell-name {
      color: var(--rose-600);
    }
  }

  .cell-name {
    font-weight: 500;
  }

  /* 收益列：本表唯一有分量的数字 */
  .cell-total {
    font-weight: 600;
    color: var(--ink-900);
    font-size: 14px;
  }

  /* 参考列（广告/赠送）：明显降级，不与收益抢注意力 */
  .cell-ref {
    color: var(--ink-350);
  }

  th.is-revenue {
    color: var(--rose-600);
    font-weight: 600;
  }

  th.is-ref {
    color: var(--ink-350);
  }
}

/* 表格脚注：一句话说清哪些列不是收益 */
.r-table__foot {
  padding: 10px 18px 14px;
  font-size: 11px;
  color: var(--ink-400);
  line-height: 1.7;

  b {
    color: var(--ink-500);
    font-weight: 500;
  }
}

.share-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;

  &__track {
    width: 76px;
    height: 5px;
    background: var(--ink-100);
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__in {
    height: 100%;
    border-radius: 3px;
    background: var(--rose-500);
  }

  &__text {
    min-width: 40px;
    text-align: right;
    font-size: 12px;
    color: var(--ink-500);
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- 赠送构成 ---------- */
.gift-list {
  padding: 14px 20px 18px;
}

.gift {
  display: grid;
  grid-template-columns: 84px 1fr 132px;
  align-items: center;
  gap: 12px;
  padding: 8px 0;

  &__label {
    font-size: 12px;
    color: var(--ink-600);
  }

  &__track {
    height: 5px;
    background: var(--ink-100);
    border-radius: 3px;
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    border-radius: 3px;
    background: var(--ink-350);
  }

  &__value {
    font-size: 12px;
    text-align: right;
    color: var(--ink-500);
    font-variant-numeric: tabular-nums;
  }
}

.gift-total {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--ink-100);
  font-size: 12px;
  color: var(--ink-500);
  line-height: 1.8;

  b {
    color: var(--ink-900);
    font-weight: 600;
  }

  em {
    font-style: normal;
    color: var(--ink-400);
  }
}

/* ---------- 对账 ---------- */
.recon {
  padding: 8px 20px 16px;

  &__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px dashed var(--ink-100);

    span {
      font-size: 12px;
      color: var(--ink-500);
    }

    b {
      font-size: 14px;
      font-weight: 500;
      color: var(--ink-900);
      font-variant-numeric: tabular-nums;
    }

    &--sum {
      border-bottom: 0;
      padding-bottom: 12px;

      span {
        color: var(--ink-900);
        font-weight: 500;
        font-size: 13px;
      }

      b {
        font-size: 22px;
        font-weight: 600;
        color: var(--rose-600);
      }
    }

    &--muted {
      span,
      b {
        color: var(--ink-400);
        font-weight: 400;
      }
    }

    /* "以下均不计入收益"这一组：比 muted 再弱一点，且统一灰 */
    &--ref {
      span,
      b {
        color: var(--ink-350);
        font-weight: 400;
      }
    }
  }

  /* 分界线：把"是收益"和"不是收益"视觉上切开 */
  &__divider {
    margin-top: 10px;
    padding: 6px 0 2px;
    font-size: 11px;
    color: var(--ink-400);
    border-top: 1px solid var(--ink-200);
  }

  &__tip {
    margin-top: 10px;
    font-size: 11px;
    color: var(--ink-400);
    line-height: 1.7;
  }
}

/* ---------- 底部操作条 ---------- */
.r-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 15px 20px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-card);

  &__note {
    font-size: 12px;
    color: var(--ink-400);
    line-height: 1.8;

    b {
      color: var(--ink-600);
      font-weight: 500;
    }
  }

  &__btns {
    display: flex;
    gap: 10px;
  }
}

.btn {
  height: 34px;
  padding: 0 16px;
  border-radius: var(--r-sm);
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink-600);
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: var(--ink-300);
    color: var(--ink-900);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* ⛔ 全页唯一的实心按钮：结账锁定是这里最需要突出的动作 */
  &--primary {
    background: var(--rose-500);
    border-color: var(--rose-500);
    color: #fff;
    font-weight: 500;

    &:hover:not(:disabled) {
      background: var(--rose-600);
      border-color: var(--rose-600);
      color: #fff;
    }
  }
}

/* ---------- 抽屉 ---------- */
.drawer-body {
  padding: 22px 24px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink-100);
  flex-shrink: 0;

  h3 {
    font-size: 17px;
    font-weight: 600;
    color: var(--ink-900);
  }

  p {
    margin-top: 6px;
    font-size: 12px;
    color: var(--ink-400);
  }
}

.drawer-close {
  border: 0;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: var(--r-sm);
  color: var(--ink-400);
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--ink-100);
    color: var(--ink-900);
  }
}

.order-list {
  margin-top: 4px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.order {
  padding: 12px 0;
  border-bottom: 1px solid var(--ink-100);

  &:last-child {
    border-bottom: none;
  }

  &__main {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  &__user {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink-900);
  }

  &__channel {
    font-size: 11px;
    color: var(--ink-500);
    background: var(--ink-100);
    border-radius: 4px;
    padding: 1px 6px;
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 5px;

    span {
      font-size: 11px;
      color: var(--ink-400);
      font-variant-numeric: tabular-nums;
    }
  }

  &__no {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__amount {
    font-size: 14px !important;
    font-weight: 600;
    color: var(--rose-600) !important;
  }
}

/* ---------- 弹窗提示 ---------- */
.dialog-tip {
  font-size: 11px;
  color: var(--ink-400);
  line-height: 1.8;
  padding: 4px 2px 0;
}

@media (max-width: 1200px) {
  .hero {
    grid-template-columns: 1fr;
    gap: 22px;
  }

  .hero__rank {
    padding-left: 0;
    border-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 18px;
  }

  .r-cols {
    grid-template-columns: 1fr;
  }
}
</style>
